/**
 * Profile Service
 * User profile, settings, and notification preferences.
 * Uses get_my_profile RPC and update_my_profile RPC where applicable.
 */

import { supabase } from '../lib/supabase';
import { formatSupabaseError } from './utils';

const BUCKET_PROFILE_IMAGES = 'profile-images';

/**
 * Get current user's profile (from RPC).
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export async function getMyProfile() {
  try {
    const { data, error } = await supabase.rpc('get_my_profile');
    if (error) throw error;
    return { data: data ?? null, error: null };
  } catch (error) {
    console.error('[ProfileService] getMyProfile error:', error);
    return {
      data: null,
      error: formatSupabaseError(error, 'Failed to load profile.'),
    };
  }
}

/**
 * Update current user's profile. Pass only fields to update.
 * Maps to update_my_profile RPC params.
 * @param {{
 *   full_name?: string,
 *   phone?: string,
 *   profile_image_url?: string,
 *   timezone?: string,
 *   notification_preferences?: object,
 *   business_profile?: { business_title?: string, bio?: string, website_url?: string, linkedin_url?: string, facebook_url?: string, instagram_url?: string, twitter_url?: string }
 * }} data
 * @returns {Promise<{ success: boolean, error: object | null }>}
 */
export async function updateMyProfile(data) {
  try {
    const {
      full_name = null,
      phone = null,
      profile_image_url = null,
      timezone = null,
      notification_preferences = null,
      business_profile = null,
    } = data ?? {};

    const { error } = await supabase.rpc('update_my_profile', {
      p_full_name: full_name ?? undefined,
      p_phone: phone ?? undefined,
      p_profile_image_url: profile_image_url ?? undefined,
      p_timezone: timezone ?? undefined,
      p_notification_preferences: notification_preferences ?? undefined,
      p_business_profile: business_profile ?? undefined,
    });

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('[ProfileService] updateMyProfile error:', error);
    return {
      success: false,
      error: formatSupabaseError(error, 'Failed to update profile.'),
    };
  }
}

/**
 * Upload profile picture to Supabase Storage (profile-images bucket).
 * Path: {userId}/{uniqueFilename}
 * @param {File} file
 * @param {string} userId - auth user id
 * @returns {Promise<{ data: { path: string, publicUrl?: string } | null, error: object | null }>}
 */
export async function uploadProfileImage(file, userId) {
  try {
    if (!file || !userId) {
      return {
        data: null,
        error: { message: 'File and user ID are required.', code: 'validation' },
      };
    }
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BUCKET_PROFILE_IMAGES)
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from(BUCKET_PROFILE_IMAGES)
      .getPublicUrl(uploadData.path);

    return {
      data: {
        path: uploadData.path,
        publicUrl: urlData?.publicUrl,
        fullPath: uploadData.fullPath ?? path,
      },
      error: null,
    };
  } catch (error) {
    console.error('[ProfileService] uploadProfileImage error:', error);
    return {
      data: null,
      error: formatSupabaseError(error, 'Failed to upload profile image.'),
    };
  }
}

/**
 * Get a signed URL for a profile image path (for private bucket).
 * @param {string} path - path in bucket (e.g. userId/filename.jpg)
 * @param {number} [expiresIn=3600]
 * @returns {Promise<{ data: string | null, error: object | null }>}
 */
export async function getProfileImageUrl(path, expiresIn = 3600) {
  try {
    if (!path) return { data: null, error: null };
    const { data, error } = await supabase.storage
      .from(BUCKET_PROFILE_IMAGES)
      .createSignedUrl(path, expiresIn);
    if (error) throw error;
    return { data: data?.signedUrl ?? null, error: null };
  } catch (error) {
    console.error('[ProfileService] getProfileImageUrl error:', error);
    return { data: null, error: formatSupabaseError(error, 'Failed to get image URL.') };
  }
}

/**
 * Update notification preferences only.
 * @param {{
 *   event_reminders?: boolean,
 *   message_notifications?: boolean,
 *   platform_updates?: boolean,
 *   reminder_timing?: '24hr' | '1hr' | 'both'
 * }} prefs
 * @returns {Promise<{ success: boolean, error: object | null }>}
 */
export async function updateNotificationPreferences(prefs) {
  return updateMyProfile({ notification_preferences: prefs });
}

/**
 * Get current user's event registrations with event details (for students on Profile page).
 * @returns {Promise<{ data: Array<{ id, event_id, event, status, payment_status, created_at, primary_agent_id }> | null, error: object | null }>}
 */
export async function getMyRegistrationsWithEvents() {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { data: null, error: authError || { message: 'Not authenticated', code: 'auth' } };
    }
    const { data, error } = await supabase
      .from('event_registrations')
      .select(`
        id, event_id, status, payment_status, created_at, primary_agent_id,
        event:events(id, title, event_slug, status)
      `)
      .eq('student_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (error) {
    console.error('[ProfileService] getMyRegistrationsWithEvents error:', error);
    return {
      data: null,
      error: formatSupabaseError(error, 'Failed to load registrations.'),
    };
  }
}

/**
 * Get an agent's referral code (for building registration links with ref=).
 * Used so students can share/register with their referring agent's code.
 * @param {string} agentId
 * @returns {Promise<{ data: string | null, error: object | null }>}
 */
export async function getAgentReferralCode(agentId) {
  try {
    if (!agentId) return { data: null, error: null };
    const { data, error } = await supabase
      .from('users')
      .select('agent_referral_code')
      .eq('id', agentId)
      .maybeSingle();
    if (error) throw error;
    return { data: data?.agent_referral_code ?? null, error: null };
  } catch (error) {
    console.error('[ProfileService] getAgentReferralCode error:', error);
    return {
      data: null,
      error: formatSupabaseError(error, 'Failed to load referral code.'),
    };
  }
}

/**
 * Get a user's public profile (full_name, email, phone) by id. Used to show agent/partner who invited the student.
 * RLS may restrict to same-org or primary_agent; for profile page we only need when we have primary_agent_id.
 * @param {string} userId
 * @returns {Promise<{ data: { full_name, email, phone } | null, error: object | null }>}
 */
export async function getAgentProfileById(userId) {
  try {
    if (!userId) return { data: null, error: null };
    const { data, error } = await supabase
      .from('users')
      .select('full_name, email, phone')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[ProfileService] getAgentProfileById error:', error);
    return {
      data: null,
      error: formatSupabaseError(error, 'Failed to load agent info.'),
    };
  }
}
